import { Component, OnInit, DestroyRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { Transition, StateService } from '@uirouter/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { CardModule } from 'primeng/card';
import { RadioButton } from 'primeng/radiobutton';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule, MultiSelectFilterEvent } from 'primeng/multiselect';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ConfirmationService, TooltipOptions } from 'primeng/api';
import { GlobalToastMessageService } from '@client/app/shared-modules/global-toast/services/global-toast-message.service';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';

import {
    SceModulesTypeof,
    ScanExclusion,
    MatchRule,
    Module,
    AccessibilityScanRule,
    GenericScanRule,
    AccessibilityScanExclusion,
    ComputedAccessibilityCheck,
    GenericScanExclusion,
} from '@client/app/feature-modules/admin/modules/admin-scan-exclusions/types/scan-exclusions.type';

import { ScanExclusionsRepo } from '@client/app/feature-modules/admin/modules/admin-scan-exclusions/services/scan-exclusions.repo';
import { AccessibilityScanExclusionsRepo } from '@client/app/feature-modules/admin/modules/admin-scan-exclusions/services/accessibility-scan-exclusions.repo';
import { ScanExclusionsService, ComputedDomain } from '@client/app/feature-modules/admin/modules/admin-scan-exclusions/services/scan-exclusions.service';

import { DEFAULT_PAGE_SIZE } from '@client/app/shared-modules/api/constants/page-size.constant';
import { OPTIMIZE_CMS } from '@client/app/shared-modules/api/constants/optimize-cms.constants';

import { ValidTargetSelectorDirective } from '@client/app/feature-modules/admin/modules/admin-scan-exclusions/directives/target-selector.directive';
import { UnsavedFormCheckService } from '@client/app/shared-modules/navigation-guard/unsaved-form-check.service';
import { ActionTriggerService } from '@client/app/shared-modules/tracking/action-trigger/action-trigger.service';
import { AdminLayoutService } from '@client/app/feature-modules/admin/services/admin-layout.service';
import { DomainService } from '@client/app/services/entities/domain/domain.service';
import { PageLayoutComponent } from '@client/app/feature-modules/shared/components/page-layout/page-layout.component';

const SEARCH_DEBOUNCE_TIME_MS = 500;

interface ScanExclusionForm {
    id: number | null;
    module: SceModulesTypeof | '';
    title: string;
    defaultForCms: string | null;
    domains: ComputedDomain[];
};

interface SavedScanExclusionFormValue {
    scanExclusionFormObj: ScanExclusionForm;
    accessibilityScanRules: AccessibilityScanRule[];
    scanRules: GenericScanRule[];
};

type CmsOption = {
    id: string | null;
    name: string;
};

@Component({
    selector: 'mon-admin-scan-exclusions-form',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        TranslocoModule,
        CardModule,
        RadioButton,
        ButtonModule,
        InputTextModule,
        SelectModule,
        MultiSelectModule,
        ProgressSpinnerModule,
        ConfirmDialog,
        TooltipModule,
        ValidTargetSelectorDirective,
        PageLayoutComponent,
    ],
    providers: [
        ScanExclusionsService,
        ConfirmationService,
        UnsavedFormCheckService,
    ],
    templateUrl: './admin-scan-exclusions-form.component.html',
    styleUrl: './admin-scan-exclusions-form.component.scss',
})
export class AdminScanExclusionsFormComponent implements OnInit {
    @ViewChild('form') form: NgForm | undefined = undefined;
    isEditMode: boolean = false;
    loadingForm: boolean = true;
    loadingDomains: boolean = false;
    savingForm: boolean = false;

    deregisterTransitionHook: Function | null = null;

    modules: Module[] = [];
    domainOptions: ComputedDomain[] = [];
    selectedDomains: ComputedDomain[] = [];
    currentSearchTerm: string = '';
    cmsOptions: CmsOption[] = [
        ...OPTIMIZE_CMS,
    ];

    private domainSearchSubject$ = new Subject<string>();
    private checksSearchSubject$ = new Subject<{ query: string; ruleIndex: number }>();
    private destroy$ = new Subject<void>();

    savedFormValue: SavedScanExclusionFormValue | null = null;
    scanExclusionFormObj: ScanExclusionForm = {
        id: null,
        module: '',
        title: '',
        defaultForCms: null,
        domains: [],
    };

    tooltipOptions: TooltipOptions = {
        tooltipEvent: 'hover',
        tooltipPosition: 'top',
        appendTo: 'body',
    };

    accessibilityScanRules: AccessibilityScanRule[] = [];
    scanRules: GenericScanRule[] = [];

    exclusionTypeOptions: MatchRule[] = [];
    accessibilityCheckOptions: ComputedAccessibilityCheck[] = [];
    filteredChecks: ComputedAccessibilityCheck[] = [];

    constructor (
        private transition: Transition,
        private readonly scanExclusionsService: ScanExclusionsService,
        private readonly destroyRef: DestroyRef,
        private readonly scanExclusionsRepo: ScanExclusionsRepo,
        private readonly accessibilityScanExclusionsRepo: AccessibilityScanExclusionsRepo,
        private readonly globalToastMessageService: GlobalToastMessageService,
        private readonly stateService: StateService,
        private readonly unsavedFormCheckService: UnsavedFormCheckService,
        private readonly translocoService: TranslocoService,
        private readonly actionTriggerService: ActionTriggerService,
        private readonly adminLayoutService: AdminLayoutService,
        private readonly domainService: DomainService,
    ) { }

    ngOnInit (): void {
        this.loadingForm = true;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const id = this.transition.params().id;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const module = this.transition.params().module;

        this.isEditMode = Boolean(id);

        // Defer breadcrumb setting to avoid ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => this.setBreadcrumbs(), 0);

        this.deregisterTransitionHook = this.unsavedFormCheckService.registerGuard(
            this.transition.router.transitionService,
            () => this.hasUnsavedChanges(),
        );

        this.destroyRef.onDestroy(() => {
            if (this.deregisterTransitionHook) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                this.deregisterTransitionHook();
            }
            this.destroy$.next();
            this.destroy$.complete();
        });

        this.domainSearchSubject$.pipe(
            debounceTime(SEARCH_DEBOUNCE_TIME_MS),
            takeUntil(this.destroy$),
        ).subscribe((searchText) => {
            this.fetchDomains(searchText);
        });

        this.checksSearchSubject$.pipe(
            debounceTime(SEARCH_DEBOUNCE_TIME_MS),
            takeUntil(this.destroy$),
        ).subscribe(({ query, ruleIndex }) => {
            this.filterChecks(query, ruleIndex);
        });

        this.initializeForm(id, module);
    }

    private setBreadcrumbs (): void {
        this.adminLayoutService.setBreadcrumbs({
            uiStates: [
                {
                    label: 'admin.scanExclusions.table.breadcrumbLabel',
                    state: 'base.customer.admin.scan-exclusions',
                },
                {
                    label: this.isEditMode
                        ? 'admin.scanExclusions.form.edit.breadcrumbLabel'
                        : 'admin.scanExclusions.form.add.breadcrumbLabel',
                    state: this.isEditMode ? 'base.customer.admin.edit-scan-exclusions' : 'base.customer.admin.add-scan-exclusions',
                },
            ],
        });
    }

    private async initializeForm (id: string, module: string): Promise<void> {
        this.scanExclusionFormObj.id = Number(id) || null;
        this.scanExclusionFormObj.module = (module as SceModulesTypeof) || 'accessibility';
        await this.setupFormOptions();

        this.isEditMode = !!(id && module);
        if (this.isEditMode) {
            await this.loadExclusionData(Number(id), module);
        } else {
            this.scanExclusionFormObj.module = 'accessibility';
            this.modules = this.scanExclusionsService.getModules();
            this.addExclusion();
        }
        this.markModelAsClean();
        this.loadingForm = false;
        for (let i = 0; i < this.accessibilityScanRules.length; i++) {
            this.searchChecks({ filter: '', originalEvent: new Event('') }, i);
        }
    }

    private markModelAsClean (): void {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        this.savedFormValue = JSON.parse(JSON.stringify({
            scanExclusionFormObj: this.scanExclusionFormObj,
            accessibilityScanRules: this.accessibilityScanRules,
            scanRules: this.scanRules,
        }));
    }

    private async loadExclusionData (id: number, module: string): Promise<void> {
        try {
            const exclusionData = await this.scanExclusionsService.fetchExclusionData(id, module);
            if (!exclusionData) {
                this.globalToastMessageService.error(
                    this.translocoService.translate('adminScanExclusions.form.scanExclusion.notFoundToast'),
                );
                this.redirectToScanExclusionsList();
                return;
            }
            this.populateForm(exclusionData, module);
        } catch (err) {
            this.globalToastMessageService.error(
                this.translocoService.translate('adminScanExclusions.form.scanExclusion.failedToast'),
            );
            this.redirectToScanExclusionsList();
            return;
        } finally {
            this.loadingForm = false;
        }
    }

    private populateForm (
        data: ScanExclusion,
        module: string,
    ) {
        if (module === 'accessibility') {
            const exclusionData = data as AccessibilityScanExclusion;
            this.scanExclusionFormObj = {
                ...this.scanExclusionFormObj,
                title: exclusionData.short_description,
                defaultForCms: exclusionData.default_for_cms,
            };
            const checkMap = new Map(this.accessibilityCheckOptions.map(opt => [opt.id, opt]));
            this.accessibilityScanRules = exclusionData.match_rules.map(rule => ({
                type: rule.type,
                value: rule.value,
                checks: rule.accessibility_check_ids
                    .map(id => checkMap.get(id))
                    .filter((c): c is ComputedAccessibilityCheck => !!c),
            }));
        } else {
            const exclusionData = data as GenericScanExclusion;
            this.scanExclusionFormObj = {
                ...this.scanExclusionFormObj,
                title: exclusionData.title,
                defaultForCms: exclusionData.default_for_cms,
            };
            this.scanRules = exclusionData.rules.map(rule => ({
                operator: rule.operator,
                value: rule.value,
            }));
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        this.savedFormValue = JSON.parse(JSON.stringify({
            scanExclusionFormObj: this.scanExclusionFormObj,
            accessibilityScanRules: this.accessibilityScanRules,
            scanRules: this.scanRules,
        }));
    }

    private async setupFormOptions () {
        this.cmsOptions.unshift({
            id: null,
            name: this.translocoService.translate('adminScanExclusions.form.cmsOptions.noneOptionLabel'),
        });
        if (this.scanExclusionFormObj.module === 'accessibility') {
            this.exclusionTypeOptions = this.scanExclusionsService.getMatchRuleOptions();
            this.accessibilityCheckOptions = await this.scanExclusionsService.fetchAccessibilityChecks();
            if (this.isEditMode) {
                const prefillDomainIds = await this.accessibilityScanExclusionsRepo.getAccessibilityScanExclusionDomains(this.scanExclusionFormObj.id);
                await this.buildInitialDomainOptions(prefillDomainIds);
            }
        } else if (this.isEditMode) {
            const prefillDomainIds = await this.scanExclusionsRepo.getScanExclusionDomains(this.scanExclusionFormObj.id);
            await this.buildInitialDomainOptions(prefillDomainIds);
        }
    }

    private async buildInitialDomainOptions (prefillDomainIds: Array<{ id: number }>) {
        const domainPromises = prefillDomainIds.map(obj => this.domainService.get(obj.id));
        const domains = await Promise.all(domainPromises);
        this.domainOptions = domains.map(domain => ({
            id: domain.id,
            title: domain.title,
            url: domain.url,
        }));
        this.scanExclusionFormObj.domains = [...this.domainOptions];
    }

    private hasUnsavedChanges (): boolean {
        if (!this.savedFormValue) {
            return false;
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const currentValue = JSON.parse(JSON.stringify({
            scanExclusionFormObj: this.scanExclusionFormObj,
            accessibilityScanRules: this.accessibilityScanRules,
            scanRules: this.scanRules,
        }));
        return JSON.stringify(currentValue) !== JSON.stringify(this.savedFormValue) && !this.savingForm;
    }

    addExclusion () {
        this.actionTriggerService.track({
            action: 'admin-scan-exclusions-add-exclusion-btn',
        });
        if (this.scanExclusionFormObj.module === 'accessibility') {
            this.accessibilityScanRules.push({
                type: '',
                value: '',
                checks: [],
            });
        } else {
            this.scanRules.push({
                operator: 'css',
                value: '',
            });
        }
    }

    get rulesArray () {
        return this.scanExclusionFormObj.module === 'accessibility'
            ? this.accessibilityScanRules
            : this.scanRules;
    }

    removeExclusion (index: number) {
        this.actionTriggerService.track({
            action: 'admin-scan-exclusions-remove-exclusion-btn',
        });
        this.rulesArray.splice(index, 1);
    }

    onModuleChange () {
        this.accessibilityScanRules = [];
        this.scanRules = [];
        this.addExclusion();
    }

    searchDomains (event: MultiSelectFilterEvent) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        const search: string = (event.filter || '').trim();
        this.currentSearchTerm = search;

        if (!search) {
            this.updateFilteredDomains();
            return;
        }
        this.domainSearchSubject$.next(search);
    }

    searchChecks (event: MultiSelectFilterEvent, ruleIndex: number) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        const query = (event.filter || '').trim();

        if (!query) {
            this.updateFilteredChecks(ruleIndex);
            return;
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        this.checksSearchSubject$.next({ query, ruleIndex });
    }

    private filterChecks (query: string, ruleIndex: number) {
        const selectedChecks = this.accessibilityScanRules[ruleIndex]?.checks || [];
        const filteredOptions = this.accessibilityCheckOptions.filter(opt =>
            opt.name.toLowerCase().includes(query.toLowerCase()),
        );

        const filteredIds = new Set(filteredOptions.map(opt => opt.id));
        const mergedOptions = [
            ...selectedChecks.filter(check => !filteredIds.has(check.id)),
            ...filteredOptions,
        ];

        this.filteredChecks = mergedOptions;
    }

    updateFilteredChecks (ruleIndex: number) {
        const selectedChecks = this.accessibilityScanRules[ruleIndex]?.checks || [];
        const selectedIds = new Set(selectedChecks.map(check => check.id));
        const unselectedChecks = this.accessibilityCheckOptions.filter(check => !selectedIds.has(check.id));
        this.filteredChecks = [...selectedChecks, ...unselectedChecks];
    }

    updateFilteredDomains () {
        const selectedDomains = this.scanExclusionFormObj.domains || [];
        if (!this.currentSearchTerm) {
            this.domainOptions = [...selectedDomains];
        } else {
            const selectedIds = new Set(selectedDomains.map(domain => domain.id));
            const unselectedDomains = this.domainOptions.filter(domain => !selectedIds.has(domain.id));
            this.domainOptions = [...selectedDomains, ...unselectedDomains];
        }
    }

    private async fetchDomains (searchText: string) {
        this.loadingDomains = true;
        try {
            const domainData = await this.scanExclusionsService.fetchDomains(searchText);

            if (searchText !== this.currentSearchTerm) {
                return;
            }

            const domainIds = new Set(domainData.map(opt => opt.id));
            const mergedOptions = [
                ...this.scanExclusionFormObj.domains.filter(domain => !domainIds.has(domain.id)),
                ...domainData,
            ];
            this.domainOptions = mergedOptions;
        } finally {
            this.loadingDomains = false;
        }
    }

    onSubmit (form: NgForm) {
        this.actionTriggerService.track({
            action: 'admin-scan-exclusions-form-submit-btn',
        });
        if (form.invalid) {
            this.globalToastMessageService.error(
                this.translocoService.translate('adminScanExclusions.form.invalidInputToast'),
            );
            return;
        }

        if (this.isEditMode) {
            this.updateExclusion();
        } else {
            this.createExclusion();
        }
    }

    private async updateExclusion () {
        this.savingForm = true;
        try {
            if (this.scanExclusionFormObj.module === 'accessibility') {
                const requestObj = this.buildAccessibilityScanRequest();
                await this.accessibilityScanExclusionsRepo.updateAccessibilityScanExclusions(requestObj);
            } else {
                const requestObj = this.buildScanRequest();
                await this.scanExclusionsRepo.updateScanExclusions(requestObj);
            }
            this.globalToastMessageService.success(
                this.translocoService.translate('adminScanExclusions.form.update.successToast'),
            );
            this.cleanupAndRedirect();
        } catch (err) {
            this.globalToastMessageService.error(
                this.translocoService.translate('adminScanExclusions.form.update.errorToast'),
            );
        } finally {
            this.savingForm = false;
        }
    }

    private async createExclusion () {
        this.savingForm = true;
        try {
            if (this.scanExclusionFormObj.module === 'accessibility') {
                const requestObj = this.buildAccessibilityScanRequest();
                await this.accessibilityScanExclusionsRepo.createAccessibilityScanExclusions(requestObj);
            } else {
                const requestObj = this.buildScanRequest();
                await this.scanExclusionsRepo.createScanExclusions(requestObj);
            }
            this.globalToastMessageService.success(
                this.translocoService.translate('adminScanExclusions.form.create.successToast'),
            );
            this.cleanupAndRedirect();
        } catch (err) {
            this.globalToastMessageService.error(
                this.translocoService.translate('adminScanExclusions.form.create.errorToast'),
            );
        } finally {
            this.savingForm = false;
        }
    }

    private buildAccessibilityScanRequest () {
        return {
            ...(this.isEditMode && { id: this.scanExclusionFormObj.id }),
            module: this.scanExclusionFormObj.module as SceModulesTypeof,
            short_description: this.scanExclusionFormObj.title,
            match_rules: this.accessibilityScanRules.map(rule => ({
                type: rule.type,
                accessibility_check_ids: rule.checks.map(check => check.id),
                value: rule.value,
            })),
            default_for_cms: this.scanExclusionFormObj.defaultForCms,
            domains: (this.scanExclusionFormObj.domains || []).map(domain => domain.id),
        };
    }

    private buildScanRequest () {
        return {
            ...(this.isEditMode && { id: this.scanExclusionFormObj.id }),
            module: this.scanExclusionFormObj.module as SceModulesTypeof,
            title: this.scanExclusionFormObj.title,
            rules: this.scanRules.map(rule => ({
                operator: rule.operator,
                value: rule.value,
            })),
            default_for_cms: this.scanExclusionFormObj.defaultForCms,
            domains: (this.scanExclusionFormObj.domains || []).map(domain => domain.id),
        };
    }

    // required for unsavedFormCheckService to track changes
    private cleanupAndRedirect () {
        if (this.form) {
            this.form.resetForm(this.scanExclusionFormObj);
        }

        this.markModelAsClean();
        this.redirectToScanExclusionsList();
    }

    private redirectToScanExclusionsList (waitForToast: boolean = true) {
        const prevState = this.transition.from();
        const prevParams = this.transition.params('from');

        if (prevState && prevState.name === 'base.customer.admin.scan-exclusions') {
            this.stateService.go(prevState.name, { ...prevParams });
        } else {
            this.stateService.go('base.customer.admin.scan-exclusions', {
                page: 1,
                page_size: DEFAULT_PAGE_SIZE,
                module: this.scanExclusionFormObj.module,
            });
        }
    }

    async onCancel () {
        this.actionTriggerService.track({
            action: 'admin-scan-exclusions-form-cancel-btn',
        });
        const shouldConfirm = this.hasUnsavedChanges();
        const confirmed = await this.unsavedFormCheckService.confirmNavigation(shouldConfirm);
        if (confirmed) {
            // Restore the initial state
            if (this.savedFormValue) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                this.scanExclusionFormObj = JSON.parse(JSON.stringify(this.savedFormValue.scanExclusionFormObj));
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                this.accessibilityScanRules = JSON.parse(JSON.stringify(this.savedFormValue.accessibilityScanRules));
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                this.scanRules = JSON.parse(JSON.stringify(this.savedFormValue.scanRules));
            }
            this.unsavedFormCheckService.unregisterGuard();
            this.redirectToScanExclusionsList(false);
        }
    }
}
